'use client';

import { FC } from 'react';
import cx from 'classnames';
import { useProductDetailPageContext } from '../../context/product-detail';
import { ProductPriceAvailability } from 'services/priceAvailability/types';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import { FixedContainer } from '../../fixed-container';
import { SavedList } from 'services/savedLists/types';
import CustomizeKitButton from '../customize-kit-button/customize-kit-button';
import { MyEquipmentList } from 'components/my-equipment/types';
import QuantityPicker from '../quantity-picker/quantity-picker';
import AddToCartButton from '../add-to-cart-button';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { CurrentItemsWithPrice } from 'services/fbtItems/types';
import { DealerPriceAvailabilityResponse } from 'services/dealerPriceAvailability/types';
import { LocationResponse } from 'services/location/types';
import { DealerResponse } from 'services/dealer';
import SavedListButton from '../saved-list-button/saved-list-button';
import { CartResponse, OrderItemsResponseKit } from 'services/cart/types';
import styles from './add-to-cart-actions.module.scss';
import { getFluidAttributeData } from 'services/cart/utils';
import { BUNDLE_BEAN } from '../../constants/constants';

interface CartActionsProps {
  part: ProductPriceAvailability;
  isUpdatePDPCTAFeatureFlag: boolean;
  isPartTypeSoftware: boolean;
  withMobileFixedActions?: boolean;
  isLoggedIn?: boolean;
  savedLists: SavedList[];
  data?: CurrentItemsWithPrice;
  userFlags?: { isLoggedIn: boolean; isInstantAccessUser: boolean };
  userHasShoppingLists?: boolean;
  isCspUser?: boolean;
  isDealerUser?: boolean;
  isInstantAccessUser?: boolean;
  isAssociateEquipmentToSavedList: boolean;
  myEquipmentList: MyEquipmentList[];
  selectedEquipmentIndex: number;
  isError: boolean;
  priceAvailabilityDrawerData?: DealerPriceAvailabilityResponse;
  dealerInformation?: DealerResponse;
  getEsites;
  availRemoveInterimStockFlag?: boolean;
  isShowSourcingDetailEnabled?: boolean;
  isUpdatePDPHeaderMobile?: boolean;
  isDeliveryEnabled?: boolean;
  userStoreLocationData?: LocationResponse;
  isHeadlessPDPEnabled?: boolean;
  orderItems?: Array<OrderItemsResponseKit>;
  contactDealerComp?: React.ReactNode;
  isGuestUser?: boolean;
  catcorp?: boolean;
  showAllList?: boolean;
  locationId?: string;
  cartDetails?: CartResponse;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  isCustomizeKitQuantityEnabled?: boolean;
  saveToListEnhancement: boolean;
  isHeadlessPriceAndAvailability: boolean;
  savedListDetailFeatureFlag?: boolean;
}

const AddToCartActions: FC<CartActionsProps> = ({
  part,
  isPartTypeSoftware = false,
  isUpdatePDPCTAFeatureFlag = false,
  withMobileFixedActions = false,
  isLoggedIn,
  savedLists,
  data,
  userFlags,
  userHasShoppingLists,
  isCspUser,
  isDealerUser,
  isInstantAccessUser,
  isAssociateEquipmentToSavedList,
  myEquipmentList,
  selectedEquipmentIndex,
  isError,
  priceAvailabilityDrawerData,
  dealerInformation,
  getEsites,
  availRemoveInterimStockFlag,
  isUpdatePDPHeaderMobile,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  userStoreLocationData,
  isHeadlessPDPEnabled,
  orderItems,
  contactDealerComp,
  isGuestUser,
  catcorp,
  showAllList,
  cartDetails,
  locationId,
  isRTL = false,
  currencyLdFeatureFlag = false,
  isCustomizeKitQuantityEnabled,
  saveToListEnhancement,
  isHeadlessPriceAndAvailability,
  savedListDetailFeatureFlag = false
}) => {
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { locale } = useProductDetailPageContext();

  const commonSavedListProps = {
    part,
    locale,
    isLoggedIn,
    userHasShoppingLists,
    isCspUser,
    isInstantAccessUser,
    isDealerUser,
    isAssociateEquipmentToSavedList,
    myEquipmentList,
    selectedEquipmentIndex,
    isError,
    savedLists,
    showAllList,
    savedListDetailFeatureFlag
  };

  const displayAddToListButton =
    !isUpdatePDPCTAFeatureFlag || !isPartTypeSoftware;

  const items = data?.components;

  const updatedProduct = items?.map((item) => ({
    ...item,
    manufacturer: data?.manufacturer,
    categoryPath: data?.categoryPath
  }));
  const { dealerCode } = getEsites;
  const dealerFluidAttributes = getFluidAttributeData(
    data?.fluidAttributes,
    dealerCode
  );
  const productType = data?.type;
  const isBundle = productType === BUNDLE_BEAN;

  return (
    <div
      className={cx(
        'd-blg-flex align-items-start py-2',
        !isBundle && 'd-flex gap-2 flex-wrap'
      )}
    >
      <Conditional test={isMD || !isUpdatePDPHeaderMobile}>
        <QuantityPicker isUpdatePDPHeaderMobile={isUpdatePDPHeaderMobile} />
      </Conditional>
      <div className="d-flex">
        <AddToCartButton
          part={part}
          withMobileFixedActions={withMobileFixedActions}
          displayAddToListButton={displayAddToListButton}
          currency= {cartDetails?.orderItemPriceAvailabilityTotals?.currency}
          priceAvailabilityData={updatedProduct}
          cartDetails={cartDetails}
          locationId={locationId}
          dealerFluidAttributes={dealerFluidAttributes}
          priceAvailabilityDrawerData={isBundle ? priceAvailabilityDrawerData : null}
        />
        <div className="d-md-block">
          <CustomizeKitButton
            data={data}
            userFlags={userFlags}
            priceAvailabilityDrawerData={priceAvailabilityDrawerData}
            dealerInformation={dealerInformation}
            getEsites={getEsites}
            availRemoveInterimStockFlag={availRemoveInterimStockFlag}
            isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
            isDeliveryEnabled={isDeliveryEnabled}
            userStoreLocationData={userStoreLocationData}
            isHeadlessPDPEnabled={isHeadlessPDPEnabled}
            orderItems={orderItems}
            contactDealerComp={contactDealerComp}
            isGuestUser={isGuestUser}
            catcorp={catcorp}
            priceAvailabilityData={updatedProduct}
            cartDetails={cartDetails}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
            isCustomizeKitQuantityEnabled={isCustomizeKitQuantityEnabled}
            isHeadlessPriceAndAvailability={isHeadlessPriceAndAvailability}
          />
        </div>
      </div>
      <Conditional test={displayAddToListButton && !withMobileFixedActions}>
        <div className="d-inline-block">
          <SavedListButton
            {...commonSavedListProps}
            size={isMD ? 'sm' : 'md'}
            variant="ghost"
            className={styles['save-to-list__btn']}
            saveToListEnhancement={saveToListEnhancement}
          />
        </div>
      </Conditional>
      <Conditional
        test={displayAddToListButton && withMobileFixedActions && isMD}
      >
        <div className="d-inline-block d-md-block ">
          <SavedListButton
            {...commonSavedListProps}
            size={isMD ? 'sm' : 'md'}
            variant="ghost"
            className={styles['save-to-list__btn']}
            saveToListEnhancement={saveToListEnhancement}
          />
        </div>
      </Conditional>
      {withMobileFixedActions && !isMD && (
        <FixedContainer
          spacerClassName="d-md-none"
          containerClassName="d-md-none"
        >
          <Conditional test={displayAddToListButton}>
            <div className="pe-1 w-50">
              <SavedListButton
                {...commonSavedListProps}
                fullWidth={true}
                saveToListEnhancement={saveToListEnhancement}
              />
            </div>
          </Conditional>
          <div
            className={cx({
              'w-50': displayAddToListButton,
              'w-100': !displayAddToListButton
            })}
          >
            <AddToCartButton
              part={part}
              withMobileFixedActions={withMobileFixedActions}
              displayAddToListButton={displayAddToListButton}
              isMobileView
              currency= {cartDetails?.orderItemPriceAvailabilityTotals?.currency}
              priceAvailabilityData={updatedProduct}
              cartDetails={cartDetails}
              locationId={locationId}
              dealerFluidAttributes={dealerFluidAttributes}
              priceAvailabilityDrawerData={priceAvailabilityDrawerData}
            />
          </div>
        </FixedContainer>
      )}
    </div>
  );
};

export default AddToCartActions;
